import React, { useState } from "react";
import { BsSpeedometer } from "react-icons/bs";
import { FiDownload, FiCheck, FiSliders, FiUsers, FiMonitor, FiCpu, FiWifi, FiPhone } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

export default function SpeedTestSection() {
  const { t, language } = useLanguage();
  const [peopleCount, setPeopleCount] = useState("medium"); // small | medium | large
  const [needs, setNeeds] = useState(["streaming"]); // streaming, gaming, work, smartHome

  const toggleNeed = (needId) => {
    if (needs.includes(needId)) {
      if (needs.length > 1) {
        setNeeds(needs.filter((n) => n !== needId));
      }
    } else {
      setNeeds([...needs, needId]);
    }
  };

  // Calculate localized recommendation
  const calculateRecommendation = () => {
    let score = 0;
    if (peopleCount === "small") score += 30;
    if (peopleCount === "medium") score += 60;
    if (peopleCount === "large") score += 90;

    if (needs.includes("streaming")) score += 20;
    if (needs.includes("gaming")) score += 30;
    if (needs.includes("work")) score += 25;
    if (needs.includes("smartHome")) score += 20;

    const unit = language === "en" ? "Mbps" : language === "ru" ? "Мбит/с" : "Մբ/վ";
    const month = language === "en" ? "month" : language === "ru" ? "месяц" : "ամիս";

    if (score <= 60) {
      const titles = {
        am: `Fiber Econom (50 ${unit})`,
        ru: `Fiber Econom (50 ${unit})`,
        en: `Fiber Econom (50 ${unit})`,
      };
      const descs = {
        am: "Հարմար է առօրյա վեբ զննարկման, սոցիալական ցանցերի և սովորական վիդեո դիտումների համար:",
        ru: "Идеально подходит для повседневного серфинга, социальных сетей и просмотра видео:",
        en: "Great for everyday web browsing, social media feeds, and standard HD streaming:",
      };
      return {
        speed: 50,
        title: titles[language] || titles.am,
        price: `4,900֏ / ${month}`,
        desc: descs[language] || descs.am,
      };
    } else if (score <= 95) {
      const titles = {
        am: `Fiber Standart կամ CATV Կարմիր (80 ${unit})`,
        ru: `Fiber Standart или CATV Красный (80 ${unit})`,
        en: `Fiber Standart or CATV Red (80 ${unit})`,
      };
      const descs = {
        am: "Օպտիմալ տարբերակ միջին ընտանիքի համար՝ Full HD վիդեոների և միաժամանակյա աշխատանքի համար:",
        ru: "Оптимальный вариант для средней семьи: Full HD видео и одновременная работа нескольких устройств:",
        en: "Optimal choice for an average family: multiple connected devices and smooth Full HD streaming:",
      };
      return {
        speed: 80,
        title: titles[language] || titles.am,
        price: `5,900֏ / ${month}`,
        desc: descs[language] || descs.am,
      };
    } else if (score <= 135) {
      const titles = {
        am: `Fiber Ultra (120 ${unit})`,
        ru: `Fiber Ultra (120 ${unit})`,
        en: `Fiber Ultra (120 ${unit})`,
      };
      const descs = {
        am: "Հիանալի է 4K հեռարձակումների, ցածր ping-ով խաղերի և տնից ակտիվ հեռավար աշխատանքի համար:",
        ru: "Отлично подходит для 4K-стриминга, онлайн-игр с низким пингом и активной удаленной работы:",
        en: "Superior choice for 4K streaming, low-latency online gaming, and demanding remote office work:",
      };
      return {
        speed: 120,
        title: titles[language] || titles.am,
        price: `7,900֏ / ${month}`,
        desc: descs[language] || descs.am,
      };
    } else {
      const titles = {
        am: `Fiber Premium (200 ${unit})`,
        ru: `Fiber Premium (200 ${unit})`,
        en: `Fiber Premium (200 ${unit})`,
      };
      const descs = {
        am: "Մաքսիմալ հզորություն բազմաթիվ ակտիվ սարքերի, ծանր ֆայլերի փոխանցման և պրոֆեսիոնալ օգտագործման համար:",
        ru: "Максимальная скорость для десятков активных устройств, тяжелых загрузок и профессионального использования:",
        en: "Maximum throughput for demanding smart homes, heavy content downloads, and professional workflows:",
      };
      return {
        speed: 200,
        title: titles[language] || titles.am,
        price: `11,900֏ / ${month}`,
        desc: descs[language] || descs.am,
      };
    }
  };

  const rec = calculateRecommendation();

  return (
    <section id="speedtest" className="w-full py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-brandRed text-xs font-bold uppercase tracking-wider mb-3">
            <BsSpeedometer size={14} />
            <span>{t("speedtest.badge")}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight uppercase">
            {t("speedtest.title")}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mt-3 leading-relaxed">
            {t("speedtest.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Card 1: Official 10 GB Speed Test */}
          <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-7 md:p-9 shadow-xl flex flex-col justify-between">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-brandRed/20 text-brandRed border border-brandRed/30 flex items-center justify-center mb-6">
                <BsSpeedometer size={32} className="animate-pulse" />
              </div>

              <span className="text-xs font-bold text-red-400 uppercase tracking-widest block">
                {t("speedtest.serverTestBadge")}
              </span>
              <h3 className="text-2xl font-black uppercase tracking-tight mt-1">
                {t("speedtest.serverTestTitle")}
              </h3>

              <p className="text-slate-300 text-sm mt-4 leading-relaxed">
                {t("speedtest.serverTestDesc")}
              </p>

              <div className="space-y-2.5 mt-6 border-t border-slate-700/60 pt-5 text-xs text-slate-300">
                <div className="flex items-center space-x-2">
                  <FiCheck className="text-emerald-400 flex-shrink-0" size={14} />
                  <span>{t("speedtest.noProxy")}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheck className="text-emerald-400 flex-shrink-0" size={14} />
                  <span>{t("speedtest.realMeasure")}</span>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <a
                href="/static/media/speed_test_file.zip"
                download
                className="w-full inline-flex items-center justify-center space-x-2 bg-brandRed hover:bg-brandRedHover text-white text-xs md:text-sm font-bold tracking-wider uppercase py-4 px-6 rounded-2xl shadow-glow-red hover:shadow-glow-red-lg transform hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              >
                <FiDownload size={18} />
                <span>{t("speedtest.downloadButton")}</span>
              </a>
            </div>
          </div>

          {/* Card 2: Interactive Needs Calculator */}
          <div id="calculator" className="lg:col-span-7 bg-white border border-slate-100 rounded-3xl p-7 md:p-9 shadow-premium flex flex-col justify-between scroll-mt-28">
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-5">
                <div>
                  <span className="text-xs font-bold text-brandRed uppercase tracking-widest block">
                    {t("speedtest.calcBadge")}
                  </span>
                  <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mt-0.5">
                    {t("speedtest.calcTitle")}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-700 flex items-center justify-center">
                  <FiSliders size={22} />
                </div>
              </div>

              {/* Selector 1: People Count */}
              <div className="mt-6">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2.5">
                  {t("speedtest.questionPeople")}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: "small", label: t("speedtest.peopleSmall"), icon: FiUsers },
                    { id: "medium", label: t("speedtest.peopleMedium"), icon: FiUsers },
                    { id: "large", label: t("speedtest.peopleLarge"), icon: FiUsers },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setPeopleCount(item.id)}
                      className={`p-3 rounded-2xl border text-xs font-bold uppercase tracking-wider flex flex-col items-center justify-center space-y-1 transition-all cursor-pointer ${
                        peopleCount === item.id
                          ? "border-brandRed bg-red-50/50 text-brandRed shadow-sm"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selector 2: Usage Profile */}
              <div className="mt-6">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2.5">
                  {t("speedtest.questionNeeds")}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {[
                    { id: "streaming", label: t("speedtest.needStreaming"), icon: FiMonitor },
                    { id: "gaming", label: t("speedtest.needGaming"), icon: FiCpu },
                    { id: "work", label: t("speedtest.needWork"), icon: FiWifi },
                    { id: "smartHome", label: t("speedtest.needSmartHome"), icon: FiSliders },
                  ].map((need) => {
                    const isSelected = needs.includes(need.id);
                    return (
                      <button
                        key={need.id}
                        onClick={() => toggleNeed(need.id)}
                        className={`p-3 rounded-2xl border text-[11px] font-bold uppercase tracking-wide flex flex-col items-center justify-center space-y-1.5 transition-all cursor-pointer ${
                          isSelected
                            ? "border-brandRed bg-red-50/50 text-brandRed shadow-sm"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        <need.icon size={15} />
                        <span>{need.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Recommendation Box */}
              <div className="mt-7 p-5 rounded-2xl bg-slate-50 border border-slate-200/80">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 pb-3">
                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-brandRed block">
                      {t("speedtest.recBadge")}
                    </span>
                    <h4 className="text-lg font-black text-slate-900 uppercase">
                      {rec.title}
                    </h4>
                  </div>
                  <div className="text-right sm:text-right">
                    <span className="text-xl font-black text-slate-900 font-bebas">
                      {rec.price}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-2.5 leading-relaxed">
                  {rec.desc}
                </p>
              </div>
            </div>

            <div className="pt-6">
              <a
                href="tel:+37460666999"
                className="w-full py-3.5 px-6 rounded-2xl bg-slate-900 hover:bg-brandRed text-white font-bold uppercase tracking-wider text-xs transition-all duration-200 cursor-pointer flex items-center justify-center space-x-2"
              >
                <FiPhone size={14} />
                <span>{t("speedtest.callOperator")}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
